/**
 * Registro unificado de pestañas — misma lógica del sistema para sidebar y rutas.
 *
 * Cada pestaña se define UNA sola vez. A partir de esa definición se generan:
 *   - ítem del sidebar (NavLink)
 *   - ruta de React Router
 *   - path permitido para el guard del módulo activo
 *
 * Si aún no existe la pantalla real, omita `component` y se usará ModuloPlaceholder
 * con el mismo PageHeader y breadcrumb que el resto de la aplicación.
 *
 * Cuando implemente la funcionalidad, solo asigne `component: <MiPantalla />`
 * en la misma entrada — la pestaña y la navegación no cambian.
 */
import ModuloPlaceholder from "@/pages/modulos/ModuloPlaceholder";

/**
 * @typedef {Object} ModuloTabDef
 * @property {string} path - Ruta absoluta (p. ej. "/visor" o "/" para Panel General)
 * @property {string} label - Texto visible en el sidebar
 * @property {string} iconKey - Clave de icono en Layout.jsx (MODULE_NAV_ICONS)
 * @property {import('react').ReactNode} [component] - Pantalla; si falta → placeholder
 * @property {string} [breadcrumbModule] - Contexto para breadcrumb del placeholder
 * @property {boolean} [end] - NavLink exact match (React Router)
 * @property {string} [testid] - data-testid del enlace lateral
 * @property {boolean} [index] - true solo para Panel General (ruta index)
 * @property {boolean} [showInNav=true] - false para rutas auxiliares sin pestaña
 * @property {boolean} [hubOnlyNav] - true: ruta accesible pero oculta en sidebar de módulos (solo Hub)
 * @property {boolean} [hubCard] - false: no aparece en tarjetas de /modulo/inicio (ruta y sidebar intactos)
 * @property {string} [navSection] - Encabezado de grupo en el sidebar (p. ej. sección del flujo operativo)
 * @property {number} [navStep] - Número de paso operativo (badge opcional en sidebar)
 * @property {string} [description] - Texto breve en tarjeta del hub de inicio del módulo
 * @property {boolean} [designerOnly] - Solo visible para platform_designer (hub y sidebar)
 */

/** Paths accesibles por ruta pero ocultos en sidebars de módulos (Panel General u otra puerta). */
export const HUB_ONLY_NAV_PATHS = new Set([
  "/ayuda",
  "/gestion-datos",
  "/exportacion-local",
]);

/** Paths de hub de módulo (vista tarjetas; no se listan como tarjeta dentro del propio hub). */
export const MODULE_INICIO_PATHS = new Set([
  "/visor/inicio",
  "/laboral/inicio",
  "/tramites/inicio",
  "/disenador/inicio",
  "/despacho/inicio",
  "/materialidad/inicio",
]);

// No usar «manual» suelto: excluía por error «Descarga manual…» y «Conciliación Manual» del hub.
const HUB_ONLY_LABEL_RE = /\b(ayuda|documentaci[oó]n)\b/i;

/**
 * Ítems de ayuda/manual/documentación: se registran como ruta pero no aparecen en sidebars de módulos.
 * @param {ModuloTabDef} tab
 */
export function isHubOnlyNavTab(tab) {
  if (!tab || tab.showInNav === false) return false;
  if (tab.hubOnlyNav === true) return true;
  const path = String(tab.path || "").split("?")[0];
  if (HUB_ONLY_NAV_PATHS.has(path)) return true;
  if (tab.iconKey === "help") return true;
  const label = String(tab.label || "");
  return HUB_ONLY_LABEL_RE.test(label);
}

/**
 * Crea una definición de pestaña normalizada.
 * @param {ModuloTabDef} def
 * @returns {ModuloTabDef}
 */
export function createModuloTab(def) {
  return {
    showInNav: true,
    ...def,
  };
}

/** Convierte "/clientes" → "clientes"; "/" con index → ruta index. */
function routePathFromTab(tab) {
  if (tab.index || tab.path === "/") return null;
  return String(tab.path || "").replace(/^\//, "");
}

/** Resuelve el elemento React: pantalla real o placeholder con la misma envoltura visual. */
function resolveTabElement(tab, contextLabel) {
  if (tab.component) return tab.component;
  return (
    <ModuloPlaceholder
      title={tab.label}
      breadcrumbModule={tab.breadcrumbModule || contextLabel}
    />
  );
}

/**
 * Genera ítems del sidebar a partir del registro de pestañas.
 * @param {ModuloTabDef[]} tabs
 */
export function tabsToNavItems(tabs) {
  return tabs
    .filter((t) => t.showInNav !== false)
    .filter((t) => !isHubOnlyNavTab(t))
    .map((tab) => ({
      to: tab.path,
      label: tab.label,
      iconKey: tab.iconKey,
      end: Boolean(tab.end),
      testid: tab.testid,
      pending: !tab.component,
      navSection: tab.navSection,
      navStep: tab.navStep,
      designerOnly: Boolean(tab.designerOnly),
    }));
}

/**
 * Tarjetas operativas para la vista de inicio del módulo (hub híbrido).
 * Excluye Panel General (/), ayuda/manual y rutas auxiliares sin nav.
 * @param {ModuloTabDef[]} tabs
 */
export function tabsToHubCards(tabs) {
  return tabs
    .filter((t) => {
      if (t.index || t.path === "/") return false;
      if (t.showInNav === false) return false;
      if (isHubOnlyNavTab(t)) return false;
      if (t.hubCard === false) return false;
      const path = String(t.path || "").split("?")[0];
      if (MODULE_INICIO_PATHS.has(path)) return false;
      return true;
    })
    .map((tab) => ({
      path: tab.path,
      label: tab.label,
      description: tab.description || "",
      iconKey: tab.iconKey,
      navSection: tab.navSection,
      navStep: tab.navStep,
      pending: !tab.component,
      testid: tab.testid || `hub-feature-${String(tab.path || "").replace(/^\//, "").replace(/\//g, "-")}`,
      designerOnly: Boolean(tab.designerOnly),
    }));
}

/**
 * Paths URL permitidos mientras el módulo está activo.
 * @param {ModuloTabDef[]} tabs
 */
export function tabsToPaths(tabs) {
  return tabs
    .filter((t) => t.showInNav !== false)
    .map((tab) => tab.path);
}

/**
 * Genera definiciones de ruta para React Router.
 * @param {ModuloTabDef[]} tabs
 * @param {string} contextLabel - Etiqueta del módulo (Ayuda, breadcrumb)
 * @param {Array<{ index?: boolean, path?: string, element: import('react').ReactNode }>} [extraRoutes]
 */
export function tabsToRoutes(tabs, contextLabel, extraRoutes = []) {
  const fromTabs = tabs.map((tab) => {
    if (tab.index || tab.path === "/") {
      return { index: true, element: resolveTabElement(tab, contextLabel) };
    }
    const path = routePathFromTab(tab);
    return { path, element: resolveTabElement(tab, contextLabel) };
  });

  return [...fromTabs, ...extraRoutes];
}

/**
 * Comprueba si pathname pertenece a las pestañas del módulo.
 * @param {string} pathname
 * @param {ModuloTabDef[]} tabs
 */
export function isPathInTabs(pathname, tabs) {
  const paths = tabsToPaths(tabs);
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Ruta por defecto al activar el módulo (primera pestaña del registro).
 * @param {ModuloTabDef[]} tabs
 */
export function defaultRouteFromTabs(tabs) {
  const first = tabs.find(
    (t) => t.showInNav !== false && !t.index && String(t.path || "").split("?")[0] !== "/",
  );
  return first?.path || "/";
}

/**
 * Factory estándar de API de módulo a partir del registro de pestañas.
 * @param {object} opts
 * @param {string} opts.id
 * @param {object} opts.card
 * @param {string} opts.contextLabel
 * @param {ModuloTabDef[]} opts.tabs
 * @param {Array} [opts.extraRoutes]
 * @param {string} [opts.lockedMessage]
 */
export function buildModuloFromTabs({ id, card, contextLabel, tabs, extraRoutes = [], lockedMessage }) {
  const allPaths = () => {
    const fromTabs = tabs.map((t) => t.path).filter(Boolean);
    const fromExtra = extraRoutes
      .filter((r) => r.path)
      .map((r) => `/${String(r.path).replace(/^\//, "")}`);
    return [...fromTabs, ...fromExtra];
  };

  const mod = {
    id,
    card,
    getNavItems: () => tabsToNavItems(tabs),
    getHubCards: () => tabsToHubCards(tabs),
    getPaths: () => tabsToPaths(tabs),
    getDefaultRoute: () => defaultRouteFromTabs(tabs),
    isPathAllowed: (pathname) => {
      const paths = allPaths();
      return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
    },
    getRoutes: () => tabsToRoutes(tabs, contextLabel, extraRoutes),
    getContextLabel: () => contextLabel,
  };
  if (lockedMessage) mod.lockedMessage = lockedMessage;
  return mod;
}
